import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Link2, Image as ImageIcon, Loader2, Undo, Quote } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function RichTextEditor({ value, onChange, "data-testid": testId }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (ref.current && value !== ref.current.innerHTML) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const exec = (cmd, arg) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    emit();
  };
  const emit = () => onChange(ref.current?.innerHTML || "");

  const insertImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const abs = `${BACKEND_URL}${data.url}`;
      exec("insertHTML", `<img src="${abs}" alt="" style="max-width:100%;border-radius:12px;margin:12px 0;" />`);
      toast.success("Image inserted");
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const addLink = () => {
    const url = window.prompt("Enter URL");
    if (url) exec("createLink", url);
  };

  const Btn = ({ icon: Icon, onClick, title, testid }) => (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={title} data-testid={testid}
      className="w-9 h-9 rounded-lg flex items-center justify-center text-forest hover:bg-sand transition-colors">
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <div className="border border-sage/40 rounded-xl bg-white overflow-hidden">
      <div className="flex items-center gap-0.5 flex-wrap border-b border-sage/25 bg-sand/50 px-2 py-1.5">
        <Btn icon={Bold} onClick={() => exec("bold")} title="Bold" testid="rte-bold" />
        <Btn icon={Italic} onClick={() => exec("italic")} title="Italic" />
        <Btn icon={Heading2} onClick={() => exec("formatBlock", "<h2>")} title="Heading" testid="rte-h2" />
        <Btn icon={Heading3} onClick={() => exec("formatBlock", "<h3>")} title="Subheading" />
        <Btn icon={Quote} onClick={() => exec("formatBlock", "<blockquote>")} title="Quote" />
        <Btn icon={List} onClick={() => exec("insertUnorderedList")} title="Bullet list" />
        <Btn icon={ListOrdered} onClick={() => exec("insertOrderedList")} title="Numbered list" />
        <Btn icon={Link2} onClick={addLink} title="Link" />
        <label className="w-9 h-9 rounded-lg flex items-center justify-center text-forest hover:bg-sand transition-colors cursor-pointer" title="Insert image" data-testid="rte-image">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
          <input type="file" accept="image/*" className="hidden" onChange={insertImage} disabled={uploading} />
        </label>
        <Btn icon={Undo} onClick={() => exec("undo")} title="Undo" />
      </div>
      <div ref={ref} contentEditable onInput={emit} data-testid={testId}
        className="min-h-[220px] max-h-[420px] overflow-auto p-4 outline-none text-brown/85 leading-relaxed prose max-w-none
          [&_h2]:font-heading [&_h2]:text-2xl [&_h2]:text-forest [&_h2]:mt-3 [&_h2]:mb-1
          [&_h3]:font-heading [&_h3]:text-xl [&_h3]:text-forest [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6
          [&_blockquote]:border-l-4 [&_blockquote]:border-sage [&_blockquote]:pl-4 [&_blockquote]:italic [&_a]:text-terracotta [&_a]:underline" />
    </div>
  );
}
